import React, { useState, useEffect, useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import RangeSummary from '../Members/TestResults/PublishedTestResultsTable/RangeSummary';
import './BiomarkerDetailModal.scss';
import { BiomarkerRangeScale } from '../BaseComponents/Biomarkers/BiomarkerRangeScale';
import BiomarkerHistoryChart from '../Alerts/ShowBiomarkersTestResultsModal/BiomarkerHistoryChart';
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-down.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/Close.svg';
import { ReactComponent as MemberIcon } from '../../assets/icons/UserRounded.svg';
import Button from '../BaseComponents/Button';
import { ReactComponent as BinIcon } from '../../assets/icons/TrashBin2.svg';
import ConfirmModal from '../BaseComponents/Modal/ConfirmModal';
import { resolveRangeData } from '../../utils/biomarkers';
import BiomarkerBinaryChart from '../Alerts/ShowBiomarkersTestResultsModal/BiomarkerBinaryChart';
import {
  deleteBiomarkerResult,
  getBiomarkersResultsByAppointmentHistoricalData
} from '../../api/biomarkers';
import {
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO,
  HistoricalBiomarkerDataElement,
  UserGenderEnum
} from '../../types/apiGeneratedTypes';
import { BiomarkerCalculationDisplay } from '../common/BiomarkerCalculationDisplay';

interface BiomarkerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  biomarkerResult: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO | null;
  memberId: string;
  memberName?: string;
  memberDob?: string | null;
  memberGender?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onResultDeleted?: () => void;
  showActions?: boolean;
}

const BiomarkerDetailModal: React.FC<BiomarkerDetailModalProps> = ({
  isOpen,
  onClose,
  biomarkerResult,
  memberId,
  memberName,
  memberDob,
  memberGender,
  onEdit,
  onDelete,
  onResultDeleted,
  showActions = true
}) => {
  const [isDescriptionOpened, setIsDescriptionOpened] = useState<boolean>(false);
  const [historicalData, setHistoricalData] = useState<HistoricalBiomarkerDataElement[]>([]);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [historyResultIdToDelete, setHistoryResultIdToDelete] = useState<number | null>(null);
  const [isDeletingHistoryResult, setIsDeletingHistoryResult] = useState(false);
  const [deleteHistoryError, setDeleteHistoryError] = useState<string | null>(null);

  const fetchHistoricalData = useCallback(async () => {
    if (!biomarkerResult || !memberId) {
      setHistoricalData([]);
      return;
    }

    try {
      const resp = await getBiomarkersResultsByAppointmentHistoricalData(
        biomarkerResult.biomarker.id,
        memberId
      );
      setHistoricalData(resp.historical_data);
    } catch (err) {
      console.error('Failed to fetch historical data:', err);
    }
  }, [biomarkerResult, memberId]);

  useEffect(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData, historyRefreshKey]);

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit();
    }
  };

  const handleDeleteHistoryResult = async () => {
    if (!historyResultIdToDelete) return;

    setIsDeletingHistoryResult(true);
    setDeleteHistoryError(null);

    try {
      await deleteBiomarkerResult([historyResultIdToDelete]);

      const deletedCurrentResult =
        historyResultIdToDelete === biomarkerResult?.result.id;

      setHistoryResultIdToDelete(null);
      setHistoryRefreshKey((prev) => prev + 1);
      onResultDeleted?.();

      if (deletedCurrentResult) {
        onClose();
      } else {
        await fetchHistoricalData();
      }
    } catch (err) {
      console.error('Failed to delete biomarker result:', err);
      setDeleteHistoryError('Error deleting result');
    } finally {
      setIsDeletingHistoryResult(false);
    }
  };

  const isBinary =
    biomarkerResult?.biomarker.biomarker_type === 'BINARY' ||
    biomarkerResult?.biomarker.name.toLowerCase().includes('anti-nuclear') === true;

  const formatHistoryValue = (value: string) => {
    if (isBinary) {
      return value === 'POSITIVE' ? 'Positive' : 'Negative';
    }
    return value;
  };

  const renderHistoricalTable = () => {
    if (!biomarkerResult || historicalData.length === 0) return null;

    return (
      <div className='show-biomarker-historical-table'>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Value</th>
              <th>Units</th>
              {showActions && <th className='history-actions-header' />}
            </tr>
          </thead>
          <tbody>
            {[...historicalData].reverse().map((item) => (
              <tr key={item.id}>
                <td>
                  {formatInTimeZone(
                    new Date(item.date),
                    'UTC',
                    'dd/MM/yyyy'
                  )}
                </td>
                <td>{formatHistoryValue(item.value)}</td>
                <td>
                  {isBinary
                    ? '-'
                    : biomarkerResult.biomarker.measurement_unit || '-'}
                </td>
                {showActions && (
                  <td className='history-delete-cell'>
                    <button
                      type='button'
                      className='history-delete-btn'
                      aria-label='Delete result'
                      onClick={(e) => {
                        e.stopPropagation();
                        setHistoryResultIdToDelete(item.id);
                      }}
                    >
                      <BinIcon className='history-delete-icon' />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const rangeData = resolveRangeData(biomarkerResult?.biomarker, {
    dob: memberDob || null,
    gender: memberGender === 'male' || memberGender === 'female'
      ? (memberGender === 'male' ? UserGenderEnum.Male : UserGenderEnum.Female)
      : null
  });

  const getBiomarkerDisplayValue = (biomarker: any, result: any) => {
    if (biomarker.biomarker_type === 'BINARY') {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }

    if (biomarker.name.toLowerCase().includes('anti-nuclear')) {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }

    return `${result.value} ${biomarker.measurement_unit}`;
  };

  const renderHistoricalChart = () => {
    if (!biomarkerResult) return null;

    if (isBinary) {
      return (
        <BiomarkerBinaryChart
          biomarker={biomarkerResult.biomarker}
          value={biomarkerResult.result.value}
          member_id={memberId}
          refreshKey={historyRefreshKey}
        />
      );
    }

    return (
      <BiomarkerHistoryChart
        rangeData={
          biomarkerResult.biomarker.biomarker_type === 'UNITS' ? null : rangeData
        }
        biomarker_id={biomarkerResult.biomarker.id}
        member_id={memberId}
        unit={biomarkerResult.biomarker.measurement_unit || undefined}
        refreshKey={historyRefreshKey}
      />
    );
  };

  const showHistoricalSection =
    isBinary ||
    biomarkerResult?.biomarker.biomarker_type === 'UNITS' ||
    !!rangeData;

  if (!isOpen || !biomarkerResult) {
    return null;
  }

  return (
    <div className='biomarker-detail-modal-overlay' onClick={onClose}>
      <div className='biomarker-detail-modal-wrapper' onClick={(e) => e.stopPropagation()}>
        <div className='biomarker-detail-modal-close-button' onClick={onClose}>
          <CloseIcon />
        </div>
        <div className='biomarker-detail-modal-container'>
          <div className='biomarker-detail-modal-header'>
            <div className='biomarker-detail-modal-title'>
              {formatInTimeZone(
                new Date(biomarkerResult.result.appointment_date),
                'UTC',
                'dd/MM/yyyy'
              )}
            </div>
            {memberName && (
              <div className='biomarker-detail-modal-member-name'>
                <MemberIcon />
                {memberName}
              </div>
            )}
          </div>
          <div className='biomarker-detail-modal-content'>
            <div className='biomarker-detail-modal'>
              <div className='biomarker-body-systems'>
                <div className='biomarker-body-systems-title'>
                  {biomarkerResult.biomarker.name}
                </div>
                <div className='biomarker-body-systems-list'>
                  {biomarkerResult.biomarker.body_systems.map(
                    (bodySystem, index) => (
                      <span
                        key={bodySystem.id}
                        className='show-biomarker-body-system'
                      >
                        {bodySystem.title}
                        {index + 1 !== biomarkerResult.biomarker.body_systems.length &&
                          ', '}
                      </span>
                    )
                  )}
                </div>
              </div>
              <div className='show-biomarker-summary'>
                <div className='show-biomarker-summary-title'>Biomarker Summary</div>
                <div className='show-biomarker-summary-summary-value'>
                  {biomarkerResult.result.summary || 'No summary'}
                </div>
                <div className='divider' style={{ marginTop: '24px' }}></div>
              </div>
              <div className='show-biomarker-current-value-container'>
                <div className='current-value-wrapper'>
                  <div className='show-biomarker-current-value-label'>
                    Current value
                  </div>
                  <div className='show-biomarker-current-value-value'>
                    <span className='show-biomarker-current-value-value-number' title={getBiomarkerDisplayValue(biomarkerResult.biomarker, biomarkerResult.result)}>
                      {getBiomarkerDisplayValue(biomarkerResult.biomarker, biomarkerResult.result)}
                    </span>
                    <RangeSummary
                      biomarker={biomarkerResult.biomarker}
                      value={biomarkerResult.result.value}
                      scale={rangeData}
                    />
                  </div>
                </div>
                {(biomarkerResult.biomarker.biomarker_type !== 'BINARY' &&
                  biomarkerResult.biomarker.biomarker_type !== 'UNITS' &&
                  !biomarkerResult.biomarker.name.toLowerCase().includes('anti-nuclear')) &&
                  rangeData && (
                    <div className='show-biomarker-current-value-chart'>
                      <BiomarkerRangeScale
                        scale={rangeData}
                        currentValue={biomarkerResult.result.value}
                        biomarker={biomarkerResult.biomarker}
                      />
                    </div>
                  )}
              </div>

              {(biomarkerResult.biomarker as any).calculated_value && (
                <BiomarkerCalculationDisplay
                  resultValue={(biomarkerResult.biomarker as any).calculated_value}
                />
              )}
              {showHistoricalSection && (
                <div className='show-biomarker-historical-section'>
                  <div className='show-biomarker-historical-value-container-title'>
                    Historical value chart
                  </div>
                  <div className='show-biomarker-historical-content'>
                    <div className='show-biomarker-historical-chart'>
                      {renderHistoricalChart()}
                    </div>
                    {renderHistoricalTable()}
                  </div>
                </div>
              )}
              <div
                className={`biomarker-description-container ${isDescriptionOpened ? 'opened-biomarker-description-container' : ''
                  }`}
              >
                <div
                  className='biomarker-description-container-title'
                  onClick={() => setIsDescriptionOpened(!isDescriptionOpened)}
                >
                  <span className='biomarker-description-container-title-text'>
                    Biomarker Description
                  </span>
                  <ArrowIcon
                    style={{
                      transform: isDescriptionOpened
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  />
                </div>
                <div className='biomarker-description-container-description'>
                  {biomarkerResult.biomarker.description || 'No description'}
                </div>
              </div>
              {showActions && (onEdit || onDelete) && (
                <div className='action-btns-container'>
                  {onEdit && (
                    <Button
                      text='Edit'
                      backgroundColor='#000000'
                      color='#ffffff'
                      onClick={handleEditClick}
                    />
                  )}
                  {onDelete && (
                    <div className='delete-btn-biomarker' onClick={handleDeleteClick}>
                      <BinIcon className='bin-icon-biomarker' />
                      <span>Delete biomarker result</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {historyResultIdToDelete !== null && (
        <ConfirmModal
          isOpen={historyResultIdToDelete !== null}
          onClose={() => {
            if (!isDeletingHistoryResult) {
              setHistoryResultIdToDelete(null);
              setDeleteHistoryError(null);
            }
          }}
          onConfirmClick={handleDeleteHistoryResult}
          title='Delete biomarker result?'
          subtitle='This action will permanently remove this result. Once deleted, it cannot be restored.'
          loading={isDeletingHistoryResult}
          error={deleteHistoryError ?? undefined}
        />
      )}
    </div>
  );
};

export default BiomarkerDetailModal;
